// services/automationApi.ts

import api from "../../../../services/api";

// =========================
// NODE CONFIGURATION APIs
// =========================

// Get all available node configurations
export const getAllNodeConfigs = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowType?: string
) => {
  const response = await api.post(`automations/nodes/all`, {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_type: workflowType,
  });
  return response?.data?.data?.data || [];
};

// Get specific node configuration by key
export const getNodeConfig = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  nodeKey: string
) => {
  const response = await api.post("automations/nodes/config", {
    signature,
    company_id: companyId,
    company_type: companyType,
    node_key: nodeKey,
  });
  return response?.data?.data?.data || null;
};

// Get dynamic data for a specific data source
export const getDynamicData = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  dataSource: string,
  params?: Record<string, any>
) => {
  const response = await api.post("automations/nodes/dynamic-data", {
    signature,
    company_id: companyId,
    company_type: companyType,
    data_source: dataSource,
    ...params,
  });
  return response?.data?.data?.data || [];
};

// Get all dynamic data for a node (all fields)
export const getNodeDynamicData = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  nodeKey: string
) => {
  const response = await api.post("automations/nodes/node-dynamic-data", {
    signature,
    company_id: companyId,
    company_type: companyType,
    node_key: nodeKey,
  });
  return response?.data?.data?.data || null;
};

// Validate node configuration
export const validateNodeConfig = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  nodeKey: string,
  config: any
) => {
  const response = await api.post("automations/nodes/validate", {
    signature,
    company_id: companyId,
    company_type: companyType,
    node_key: nodeKey,
    config: config,
  });
  return response?.data?.data?.data || null;
};

// =========================
// WORKFLOW MANAGEMENT APIs
// =========================

// Save or update workflow
export const saveWorkflow = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflow: any,
  userId?: number
) => {
  const response = await api.post("automations/save", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow: workflow,
    user_id: userId,
  });
  return response?.data?.data || null;
};

// Get all workflows with pagination and filters
export const getAllWorkflows = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    workflow_type?: string;
  }
) => {
  const response = await api.post(`automations/list`, {
    signature,
    company_id: companyId,
    company_type: companyType,
    query_params: {
      page: params?.page || 1,
      limit: params?.limit || 20,
      search: params?.search || "",
      workflow_type: params?.workflow_type || "",
    },
  });
  return response?.data?.data || { workflows: [], pagination: {} };
};

// Get single workflow by ID
export const getWorkflow = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number
) => {
  const response = await api.post("automations/get", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
  });
  return response?.data?.data || null;
};

// Delete workflow
export const deleteWorkflow = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number,
  userId?: number
) => {
  const response = await api.post("automations/delete", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
    user_id: userId,
  });
  return response?.data?.data || null;
};

// Toggle workflow active status
export const toggleWorkflowStatus = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number,
  isActive: boolean
) => {
  const response = await api.post("automations/toggle-status", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
    is_active: isActive,
  });
  return response?.data?.data || null;
};

// Update workflow settings
export const updateWorkflowSettings = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number,
  settings: {
    timeout_seconds?: number;
    retry_enabled?: boolean;
    retry_max_attempts?: number;
    retry_delay_seconds?: number;
    retry_on_errors?: string[];
    on_error?: string;
    error_notification_emails?: string[];
    max_concurrent_executions?: number;
    log_level?: string;
  }
) => {
  const response = await api.post("automations/settings/update", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
    settings: settings,
  });
  return response?.data?.data || null;
};

export const getWorkflowById = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number
) => {
  const response = await api.post("automations/get-by-id", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
  });
  return response?.data?.data?.data || null;
};

// Toggle workflow active status
export const runWorkflowManually = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number
) => {
  const response = await api.post("automations/run-manually", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
  });
  return response?.data?.data || null;
};

// =========================
// WORKFLOW EXECUTION APIs
// =========================

// Get workflow execution history

export const getExecutionHistory = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number,
  page: number = 1,
  limit: number = 10
) => {
  const response = await api.post(
    `automations/executions/history?page=${page}&limit=${limit}`,
    {
      signature,
      company_id: companyId,
      company_type: companyType,
      workflow_id: workflowId,
      page,
      limit,
    }
  );
  return response?.data?.data || { data: [], pagination: {} };
};

// Get single execution details
export const getExecutionDetails = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  executionId: number
) => {
  const response = await api.post("automations/executions/details", {
    signature,
    company_id: companyId,
    company_type: companyType,
    execution_id: executionId,
  });
  return response?.data?.data || null;
};

// =========================
// WORKFLOW VERSION APIs
// =========================

// Get workflow version history
export const getVersionHistory = async (
  signature: string | null,
  companyId: string,
  companyType: string,
  workflowId: number
) => {
  const response = await api.post("automations/versions/history", {
    signature,
    company_id: companyId,
    company_type: companyType,
    workflow_id: workflowId,
  });
  return response?.data?.data || [];
};

// =========================
// WORKFLOW VALIDATION APIs
// =========================

// Validate workflow structure
export const validateWorkflow = async (
  signature: string | null,
  workflow: any
) => {
  const response = await api.post("automations/validate", {
    signature,
    workflow: workflow,
  });
  return response?.data?.data || null;
};
